import { Box, Button, Card, CardContent, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography, Autocomplete, CircularProgress } from "@mui/material";
import UploadIcon from '@mui/icons-material/Upload';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useState, useEffect, forwardRef, useImperativeHandle } from "react"
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppSelector, useAppDispatch } from '../../../../app/hooks'
import { clearPrelimApplication, createPrelimApplicationAsync, getPrelimApplicationData, PrelimApplicationState, selectPrelimApplication, updatePrelimApplicationAsync } from "./prelimApplicationDataSlice";
import { defaultIPrelimApplicationData, IPrelimApplicationData } from "./IPrelimApplicationData";
import { wrapArgument } from "../../../../lib/api-status/actionWrapper";
import uuid from "react-uuid";
import { FetchStatus } from "../../../../lib/api-status/IStatus";
import { useAsyncError, useNavigate, useParams } from "react-router-dom";
import DocumentChip from "../../../../components/DocumentChip";
import MasterData from "../../../../components/master-data/MasterData";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FormHelperText from '@mui/material/FormHelperText';
import dayjs, { Dayjs } from "dayjs";
import { selectUsers } from '../../../admin/adminSlice';
import { selectSelfRatings } from "../selfRating/selfRatingSlice";
import { fetchSelfRatingAsync } from "../selfRating/selfRatingSlice";
import { FIELD_LIMITS, checkScript, htmlTagsNotAllowed, freeformRegx, wordLimit, getCharCount, getWordCount, standardNumericValidation, blockOverflowNumericInput } from "../../../../utils/validationUtils";

interface PrelimApplicationProps {
    prelimApplicationId: String | undefined,
    setPrelimApplicationId: (id: String | undefined) => void;
    onSaveSuccess?: () => void;
}

/** Use for controlled numeric fields and “amount in words” helper text — `value || ''` treats 0 as empty and clears the input after blur. */
function hasNumericFormValue(v: unknown): boolean {
    if (v === '' || v === undefined || v === null) return false;
    return !Number.isNaN(Number(v));
}

const PrelimApplicationData = forwardRef((props: PrelimApplicationProps, ref) => {
    const { id } = useParams();
    const usersState = useAppSelector(selectUsers);
    const selfRatingState = useAppSelector(selectSelfRatings);
    const selectedScheme = useAppSelector(state => state.eligibilityQuestioner.scheme);
    const prelimApplicationState: PrelimApplicationState = useAppSelector(selectPrelimApplication);
    const [prelimApplicationFormData, setPrelimApplicationFormData] = useState<IPrelimApplicationData>(prelimApplicationState.prelimApplication || defaultIPrelimApplicationData);
    const [actionUid] = useState(uuid());
    const [prelimAppicationId, setPrelimApplicationId] = useState(props.prelimApplicationId);

    const [fundManager] = useState(prelimApplicationState.prelimApplication?.fundManager);
    const resolvedPrelimId = Number(prelimAppicationId || id);

    const MIN_DATE = dayjs("2020-01-01");
    const MAX_DATE = dayjs();

    const dispatch = useAppDispatch()

    const dealSectorOptions = [
        "Automobile And Auto Components",
        "Capital Goods",
        "Chemicals",
        "Construction & Construction Materials",
        "Consumer Services",
        "Fast Moving Consumer Goods",
        "Financial Services",
        "Healthcare",
        "Information Technology",
        "Metals & Mining",
        "Oil, Gas & Consumable Fuels",
        "Power",
        "Realty",
        "Services",
        "Telecommunication",
        "Textiles"
    ];

    const managerTypeValue = String(selfRatingState?.selfRatings?.managerType || '').trim().toLowerCase();
    const firstTimeManagerType = managerTypeValue
        ? managerTypeValue.includes("first time")
        : String(prelimApplicationFormData.fundManager || '').trim().toLowerCase() === "first time fund manager";
    const fundManagerOptions = firstTimeManagerType
        ? ["First Time Fund Manager"]
        : ["Two Funds Managed", "More Than Two Funds Managed"];

    useEffect(() => {
        if (!resolvedPrelimId) return;
        dispatch(getPrelimApplicationData(
            wrapArgument(actionUid, resolvedPrelimId)
        ));
        dispatch(fetchSelfRatingAsync(
            wrapArgument(actionUid, resolvedPrelimId)
        ));
    }, [resolvedPrelimId, actionUid, dispatch])

    useEffect(() => {
        const currentFundManager = String(prelimApplicationFormData.fundManager || '');
        if (!fundManagerOptions.includes(currentFundManager)) {
            const nextFundManager = firstTimeManagerType ? "First Time Fund Manager" : "Two Funds Managed";
            setValue("fundManager", nextFundManager, { shouldValidate: false, shouldDirty: false });
            setPrelimApplicationFormData(prev => ({ ...prev, fundManager: nextFundManager as any }));
        }
    }, [firstTimeManagerType, selfRatingState?.selfRatings?.managerType, prelimApplicationFormData.fundManager]);

    const [aifNameData, setAifNameData] = useState('');
    useEffect(() => {
        if (prelimApplicationState.status.fetchStatus === FetchStatus.IDLE && prelimApplicationState.prelimApplication?.id) {
            let data = { ...prelimApplicationState.prelimApplication };
            const initialAssessmentFundTypeRaw = selfRatingState?.selfRatings?.fundType;
            const initialAssessmentFundType =
                initialAssessmentFundTypeRaw === 'Equity Oriented Fund'
                    ? 'Equity Oriented AIF'
                    : initialAssessmentFundTypeRaw === 'Debt Oriented Fund'
                        ? 'Debt Oriented AIF'
                        : initialAssessmentFundTypeRaw;

            // Auto-populate from user state if field is empty
            if (!data.nameOfTheFund && usersState.me?.companyName) {
                data.nameOfTheFund = usersState.me.companyName;
            }
            // Auto-populate Scheme Name from user state if field is empty
            if (!data.scheme && usersState.me?.schemeName) {
                data.scheme = usersState.me.schemeName;
            }
            // Default AIF category type from Initial Assessment selection when empty.
            if (!data.aifCategoryType && initialAssessmentFundType) {
                data.aifCategoryType = initialAssessmentFundType as any;
            }
            // Keep Fund Manager in sync with First time IM/AMC selection on refresh/load.
            if (managerTypeValue.includes("first time")) {
                data.fundManager = "First Time Fund Manager" as any;
            } else if (!data.fundManager || String(data.fundManager).trim() === "First Time Fund Manager") {
                data.fundManager = "Two Funds Managed" as any;
            }

            // Convert date strings to Dayjs objects for the form
            if (data.dateOfFilingWithSEBI) {
                data.dateOfFilingWithSEBI = dayjs(data.dateOfFilingWithSEBI as any);
            }
            if (data.sdFirstClosingDomesticAmountDate) {
                data.sdFirstClosingDomesticAmountDate = dayjs(data.sdFirstClosingDomesticAmountDate as any);
            }

            const isInitialLoad = !prelimApplicationFormData?.id;
            setPrelimApplicationFormData(data);
            reset(data, { keepDirtyValues: !isInitialLoad });
            setPrelimApplicationId(String(data.id));
        }

        if (usersState.me) {
            setAifNameData(usersState.me.companyName || '');

            // If the form data exists but name is missing, and we have user data, sync it
            if (prelimApplicationState.prelimApplication?.id && !prelimApplicationFormData.nameOfTheFund && usersState.me.companyName) {
                setValue("nameOfTheFund", usersState.me.companyName, { shouldValidate: true });
                setPrelimApplicationFormData(prev => ({ ...prev, nameOfTheFund: usersState.me.companyName || '' }));
            }
            // If the form data exists but scheme is missing, and we have user data, sync it
            if (prelimApplicationState.prelimApplication?.id && !prelimApplicationFormData.scheme && usersState.me.schemeName) {
                setValue("scheme", usersState.me.schemeName, { shouldValidate: false });
                setPrelimApplicationFormData(prev => ({ ...prev, scheme: usersState.me.schemeName || '' }));
            }
        }
    }, [prelimApplicationState.prelimApplication?.id, prelimApplicationState.status.fetchStatus, usersState.me, selfRatingState?.selfRatings?.fundType, managerTypeValue, selectedScheme])

    const setDateValue = (key: string, value: any) => {
        let copiedValue: IPrelimApplicationData = { ...prelimApplicationFormData };
        (copiedValue as any)[key] = value;

        setValue(key as keyof IPrelimApplicationData, value, { shouldValidate: true });

        setPrelimApplicationFormData(copiedValue)
    };

    const hasChanged = (current: any, original: any) => {
        if (!original) return true;
        // Compare keys that exist in current to see if they differ from original
        return Object.keys(current).some(key => {
            const val1 = current[key];
            const val2 = original[key];

            // Handle dayjs objects
            if (dayjs.isDayjs(val1) || dayjs.isDayjs(val2)) {
                return !dayjs(val1).isSame(dayjs(val2));
            }

            // Normal comparison (handle null vs undefined vs "")
            const normalizedVal1 = (val1 === null || val1 === undefined) ? "" : String(val1);
            const normalizedVal2 = (val2 === null || val2 === undefined) ? "" : String(val2);

            return normalizedVal1 !== normalizedVal2;
        });
    };


    const savePrelimApplicationForm = async (data: IPrelimApplicationData, silent?: boolean) => {
        // console.log("onSubmit called", data);
        if (data.id) {
            await dispatch(updatePrelimApplicationAsync(wrapArgument(actionUid, data)));
            if (!silent && props.onSaveSuccess) {
                props.onSaveSuccess();
            }
        } else {
            const resultAction = await dispatch(createPrelimApplicationAsync(wrapArgument(actionUid, data)));
            if (createPrelimApplicationAsync.fulfilled.match(resultAction)) {
                if (!silent && props.onSaveSuccess) {
                    props.onSaveSuccess();
                }
            }
        }
    };

    const handleChange = (ev: any) => {
        if (ev.preventDefault) {
            ev.preventDefault();
        }

        let value = ev.target.value;
        const id = ev.target.id || ev.target.name;

        if (id === 'termOfFund') {
            value = value.replace(/[^0-9.]/g, '');
            const dotCount = (value.match(/\./g) || []).length;
            if (dotCount > 1) {
                const parts = value.split('.');
                value = parts[0] + '.' + parts.slice(1).join('');
            }
            if (value.includes('.')) {
                const parts = value.split('.');
                if (parts[1].length > 2) {
                    value = parts[0] + '.' + parts[1].slice(0, 2);
                }
            }
            if (value !== '' && !Number.isNaN(parseFloat(value)) && parseFloat(value) > 200) {
                value = '200';
            }
        }

        if (id === 'commitmentPeriod') {
            value = value.replace(/[^0-9.]/g, '');
            const dotCount = (value.match(/\./g) || []).length;
            if (dotCount > 1) {
                const parts = value.split('.');
                value = parts[0] + '.' + parts.slice(1).join('');
            }
            if (value.includes('.')) {
                const parts = value.split('.');
                if (parts[1].length > 2) {
                    value = parts[0] + '.' + parts[1].slice(0, 2);
                }
            }
            if (value !== '' && !Number.isNaN(parseFloat(value))) {
                const n = parseFloat(value);
                if (n > 10) value = '10';
                if (n < 0) value = '0';
            }
        }

        if (id === 'sdDescription') {
            value = value.replace(/[^0-9.]/g, '');
            const dotCount = (value.match(/\./g) || []).length;
            if (dotCount > 1) {
                const parts = value.split('.');
                value = parts[0] + '.' + parts.slice(1).join('');
            }
            if (value.includes('.')) {
                const parts = value.split('.');
                if (parts[1].length > 2) {
                    value = parts[0] + '.' + parts[1].slice(0, 2);
                }
            }
        }

        if (monthFields.includes(id)) {
            value = value.replace(/[^0-9.]/g, '');
        }

        if (percentageFields.includes(id)) {
            if (value !== '') {
                const numVal = parseFloat(value);
                if (numVal < 0) value = '0';
                if (numVal > 100) value = '100';
                if (value.includes('.')) {
                    const parts = value.split('.');
                    if (parts[1].length > 2) {
                        value = parts[0] + '.' + parts[1].slice(0, 2);
                    }
                }
            }
        }

        if (numericFields.includes(id) && !monthFields.includes(id) && !percentageFields.includes(id)) {
            if (id === 'contributionSought') {
                value = value.replace(/[^0-9.]/g, '');
                const dotCount = (value.match(/\./g) || []).length;
                if (dotCount > 1) {
                    const parts = value.split('.');
                    value = parts[0] + '.' + parts.slice(1).join('');
                }
                if (value !== '' && !isNaN(parseFloat(value))) {
                    if (parseFloat(value) > 10000) {
                        value = '10000';
                    }
                }
            }

            if (value.includes('.')) {
                const parts = value.split('.');
                if (parts[1].length > 2) {
                    value = parts[0] + '.' + parts[1].slice(0, 2);
                }
            }
        }

        let copiedValue: IPrelimApplicationData = { ...prelimApplicationFormData };

        if (ev.target.id) {
            (copiedValue as unknown as Record<string, unknown>)[ev.target.id] = value;
        } else {
            (copiedValue as unknown as Record<string, unknown>)[ev.target.name] = value;
        }

        setValue((ev.target.id || ev.target.name) as any, value);

        if (id === 'sdTargetCorpusDomestic' || id === 'sdTargetCorpusOverseas') {
            const domestic = id === 'sdTargetCorpusDomestic' ? value : (copiedValue.sdTargetCorpusDomestic ?? '');
            const overseas = id === 'sdTargetCorpusOverseas' ? value : (copiedValue.sdTargetCorpusOverseas ?? '');
            const total = (parseFloat(String(domestic)) || 0) + (parseFloat(String(overseas)) || 0);
            copiedValue.sdTotalTargetCorpus = total;
            setValue('sdTotalTargetCorpus', total as any);
        }

        if (id === 'sdGreenShoeTargetCorpusDomestic' || id === 'sdGreenShoeTargetCorpusOverseas') {
            const domestic = id === 'sdGreenShoeTargetCorpusDomestic' ? value : (copiedValue.sdGreenShoeTargetCorpusDomestic ?? '');
            const overseas = id === 'sdGreenShoeTargetCorpusOverseas' ? value : (copiedValue.sdGreenShoeTargetCorpusOverseas ?? '');
            const total = (parseFloat(String(domestic)) || 0) + (parseFloat(String(overseas)) || 0);
            copiedValue.sdGreenShoeTotalTargetCorpus = total;
            setValue('sdGreenShoeTotalTargetCorpus', total as any);
        }

        setPrelimApplicationFormData(copiedValue)
    };

    const numberToWordsIndian = (input: any) => {
        if (input === null || input === undefined) return '';
        const str = String(input).replace(/[, ]+/g, '').trim();
        if (str === '') return '';
        const num = Math.floor(Number(str));
        if (Number.isNaN(num)) return '';
        if (num === 0) return 'Zero';

        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const convert = (n: number): string => {
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
            if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
            if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
            return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
        }

        if (num !== undefined && convert(num) != undefined) {
            const result = convert(num).trim();
            return result ? result + ' Rupees only' : '';
        } else {
            return ''
        }
    }

    const handleSelectChange = (id: string, value: any) => {
        let copiedValue: IPrelimApplicationData = { ...prelimApplicationFormData };
        (copiedValue as any)[id] = value;
        setValue(id as any, value);
        setPrelimApplicationFormData(copiedValue);
    }

    const handleFirstClosingChange = (value: boolean) => {
        let copiedValue: IPrelimApplicationData = { ...prelimApplicationFormData };
        copiedValue.firstClosing = value;
        setPrelimApplicationFormData(copiedValue);
        setValue("firstClosing", value);
    };

    const percentageFields = [
        'preferredReturn', 'otherExpenses', 'targetReturnIRR', 'managementFees',
        'carriedInterest', 'hurdleCarryInterestRate'
    ];

    const monthFields = [
        'termOfFund',
    ];

    const numericFields = [
        'contributionSought',
        'sdDescription', 'sdTargetCorpusDomestic', 'sdTargetCorpusOverseas',
        'sdTotalTargetCorpus', 'sdGreenShoeTargetCorpusDomestic',
        'sdGreenShoeTargetCorpusOverseas', 'sdGreenShoeTotalTargetCorpus',
        'sdFirstClosingDomesticAmount', 'sdFirstClosingOverseasAmount',
        'fundSetupCost',
        ...percentageFields,
        ...monthFields
    ];

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF671F',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF671F',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#FF671F',
        },
        '& .MuiFormLabel-asterisk': { display: 'none' }
    };

    const numericSx = {
        ...fieldSx,
        '& .MuiOutlinedInput-root': {
            ...fieldSx['& .MuiOutlinedInput-root'],
            '&.Mui-readOnly, & .MuiInputBase-input[readOnly]': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
            }
        },
    };

    const controlSx = {
        color: '#FF671F',
        '&.Mui-checked': {
            color: '#FF671F',
        },
    };

    const handleBlur = (ev: any) => {
        const id = ev.target.id || ev.target.name;
        let value = ev.target.value;

        if (monthFields.includes(id)) {
            return; // No formatting for months
        }

        if (id === 'commitmentPeriod' && value !== '') {
            const numValue = parseFloat(value);
            if (!Number.isNaN(numValue)) {
                const clamped = Math.min(10, Math.max(0, numValue));
                const formattedValue = clamped.toFixed(2);
                setValue(id as any, formattedValue);
                setPrelimApplicationFormData((prev) => ({
                    ...prev,
                    [id]: Number(formattedValue),
                }));
            }
            return;
        }

        if (numericFields.includes(id) && value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                const formattedValue = numValue.toFixed(2);
                setValue(id as any, formattedValue);

                const updateTotals = (valId: string, totalId: string, domesticId: string, overseasId: string) => {
                    const values = getValues();
                    const domesticRaw = valId === domesticId ? formattedValue : values[domesticId as keyof IPrelimApplicationData];
                    const overseasRaw = valId === overseasId ? formattedValue : values[overseasId as keyof IPrelimApplicationData];
                    const domestic = domesticRaw === '' || domesticRaw === undefined || domesticRaw === null ? 0 : domesticRaw;
                    const overseas = overseasRaw === '' || overseasRaw === undefined || overseasRaw === null ? 0 : overseasRaw;
                    const total = (parseFloat(String(domestic)) || 0) + (parseFloat(String(overseas)) || 0);
                    const formattedTotal = total.toFixed(2);
                    setValue(totalId as any, formattedTotal as any);
                    setPrelimApplicationFormData(prev => ({
                        ...prev,
                        [valId]: Number(formattedValue),
                        [totalId]: Number(formattedTotal)
                    }));
                };

                if (id === 'sdTargetCorpusDomestic' || id === 'sdTargetCorpusOverseas') {
                    updateTotals(id, 'sdTotalTargetCorpus', 'sdTargetCorpusDomestic', 'sdTargetCorpusOverseas');
                } else if (id === 'sdGreenShoeTargetCorpusDomestic' || id === 'sdGreenShoeTargetCorpusOverseas') {
                    updateTotals(id, 'sdGreenShoeTotalTargetCorpus', 'sdGreenShoeTargetCorpusDomestic', 'sdGreenShoeTargetCorpusOverseas');
                } else {
                    setPrelimApplicationFormData(prev => ({
                        ...prev,
                        [id]: Number(formattedValue)
                    }));
                }
            }
        }
    };

    const dealSubSectorValues = {
        "26": {
            values: [
                "Other",
                "Blue Collar Outsourcing",
                "Equipment Rental",
                "Oil and Gas Services",
                "Specialised BPO or IT Services",
                "Testing and Inspection",
                "Transport Logistics",
                "Water Waste Management",
                "White Collar Outsourcing",
            ]
        },
        "27": {
            values: [
                "Other",
                "Apparel",
                "Education",
                "Food and Beverage Producers",
                "Food Retailers",
                "General Retailers",
                "Healthcare",
                "Home and Personal Care",
                "Household Durables",
                "Leisure",
                "Restaurants",
            ]
        },
        "28": {
            values: [
                "Other",
                "Banking",
                "FS Infrastructure and Payments",
                "Specialty Credit",
                "Wealth and Distribution",
            ]
        },
        "29": {
            values: [
                "Other",
                "Construction and Building Equipment",
                "Construction and Building Materials",
                "Electronics",
                "Farm and Agriculture",
                "Industrial Consumables",
                "Medical Technology",
                "Paper, Packaging and Print",
                "Power",
                "Transportation Equipment",
                "Water Waste Management",
            ]
        },
        "30": {
            values: [
                "Other", "Distribution", "Renewables", "Non Renewables", "Transport"
            ]
        },
        "31": {
            values: [
                "Other", "Hospitality", "Industrial", "Office", "Residential", "Retail",
            ]
        },
        "32": {
            values: [
                "Other",
                "Agribusiness",
                "Media", "Minerals, Oil and Gas",
                "Regional or Sector Funds",
                "Telecoms"
            ]
        }
    }

    // console.log((dealSubSectorValues as any)[String(prelimApplicationFormData?.dealSector || 0)]?.values, prelimApplicationFormData?.dealSubsector);

    const checkScript = (value: any) => !value || !value.match(/<[^> ]*>/);

    const validationSchema = Yup.object().shape({
        nameOfTheFund: Yup.string().required("Name of the Fund is required").test("check-script", htmlTagsNotAllowed, checkScript).matches(freeformRegx, "HTML/XML tags and braces (<, >, {, }, [, ]) are not allowed").nullable(),
        sponsor: Yup.string().required("Sponsor is required").test("check-script", htmlTagsNotAllowed, checkScript).matches(freeformRegx, "HTML/XML tags and braces (<, >, {, }, [, ]) are not allowed").max(FIELD_LIMITS.SHORT_TEXT, "Sponsor cannot exceed " + FIELD_LIMITS.SHORT_TEXT + " characters").nullable(),
        investmentManager: Yup.string().required("Investment Manager is required").test("check-script", htmlTagsNotAllowed, checkScript).matches(freeformRegx, "HTML/XML tags and braces (<, >, {, }, [, ]) are not allowed").max(FIELD_LIMITS.SHORT_TEXT, "Investment Manager cannot exceed " + FIELD_LIMITS.SHORT_TEXT + " characters").nullable(),
        fundManager: Yup.string().required("Fund Manager is required").nullable(),
        // dealType: Yup.string(),
        // impact: Yup.string(),
        aifCategory: Yup.string().required("AIF Category is required").nullable(),
        dateOfFilingWithSEBI: Yup.date()
            .nullable()
            .transform((curr, orig) => orig === '' ? null : curr)
            .typeError("Please enter a valid date")
            .min(new Date(2020, 0, 1), "Date cannot be before 01/01/2020")
            .max(new Date(), "Date cannot be a future date")
            .required("This value is required"),
        dealSector: Yup.string().required("Deal Sector is required").test("check-script", htmlTagsNotAllowed, checkScript).matches(freeformRegx, "HTML/XML tags and braces (<, >, {, }, [, ]) are not allowed").test("word-limit", "Deal Sector cannot exceed " + FIELD_LIMITS.LONG_TEXT + " words", wordLimit(FIELD_LIMITS.LONG_TEXT)).nullable(),
        // dealSubsector: Yup.string().required("Deal Sub Sector is required").nullable(),
        nameOfTrustee: Yup.string().required("Name of Trustee is required").test("check-script", htmlTagsNotAllowed, checkScript).matches(freeformRegx, "HTML/XML tags and braces (<, >, {, }, [, ]) are not allowed").max(FIELD_LIMITS.SHORT_TEXT, "Name of Trustee cannot exceed " + FIELD_LIMITS.SHORT_TEXT + " characters").nullable(),
        contributionSought: Yup.string().required("Contribution Sought is required").test("max-value", "Contribution Sought cannot exceed Total Target Corpus", function (value) {
            if (!value) return true;
            const targetCorpus = this.parent.sdTotalTargetCorpus;
            if (targetCorpus === undefined || targetCorpus === null) return true;
            return parseFloat(value) <= parseFloat(targetCorpus);
        }).nullable(),
        termOfFund: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).typeError("Term of Fund must be a number").required("Term of Fund is required").min(0, "Negative values not allowed").max(FIELD_LIMITS.SHORT_TEXT, "Term of Fund cannot exceed 200"),
        commitmentPeriod: Yup.number()
            .transform((val) => (isNaN(val) ? undefined : val))
            .typeError("Commitment Period must be a number")
            .required("Commitment Period is required")
            .min(0, "Negative values not allowed")
            .max(10, "Commitment Period cannot exceed 10 years")
            .test(
                'max-two-decimals',
                'Maximum 2 decimal places allowed',
                (val) => {
                    if (val === undefined || val === null) return true;
                    const rounded = Math.round(val * 100) / 100;
                    return Math.abs(val - rounded) < 1e-9;
                }
            ),
        preferredReturn: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).typeError("Preferred Return must be a number").required("Preferred Return is required").min(0, "Negative values not allowed").max(100, "Percentage cannot exceed 100"),
        managementFees: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).typeError("Management Fees must be a number").required("Management Fees is required").min(0, "Negative values not allowed").max(100, "Percentage cannot exceed 100"),
        carriedInterest: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).typeError("Carried Interest must be a number").required("Carried Interest is required").min(0, "Negative values not allowed").max(100, "Percentage cannot exceed 100"),
        description: Yup.string().required("Description is required").test("check-script", htmlTagsNotAllowed, checkScript).matches(freeformRegx, "HTML/XML tags and braces (<, >, {, }, [, ]) are not allowed").test("word-limit", "Description cannot exceed " + FIELD_LIMITS.LONG_TEXT + " words", wordLimit(FIELD_LIMITS.LONG_TEXT)).nullable(),
        // investmentStrategy: Yup.string().required("Investment Strategy is required").test("check-script", htmlTagsNotAllowed, checkScript).nullable(),
        sdDescription: standardNumericValidation("Capital raised till date"),
        sdTargetCorpusDomestic: standardNumericValidation("Domestic"),
        sdTargetCorpusOverseas: standardNumericValidation("Overseas"),
        sdTotalTargetCorpus: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).required("Total Target Corpus is required").min(100, "Value less than Rs. 100 crores shall not be allowed"),
        sdGreenShoeTargetCorpusDomestic: standardNumericValidation("Domestic (Green Shoe)"),
        sdGreenShoeTargetCorpusOverseas: standardNumericValidation("Overseas (Green Shoe)"),
        sdGreenShoeTotalTargetCorpus: standardNumericValidation("Total Target Corpus (Green Shoe)"),
        sdFirstClosingDomesticAmount: standardNumericValidation("Domestic Amount"),
        sdFirstClosingOverseasAmount: standardNumericValidation("Overseas Amount"),
        sdFirstClosingDomesticAmountDate: Yup.date()
            .nullable()
            .transform((curr, orig) => orig === '' ? null : curr)
            .typeError("Please enter a valid date")
            .min(new Date(2020, 0, 1), "Date cannot be before 01/01/2020")
            .required("This value is required"),
        // sdFirstCorpusOverseasAmountDate: Yup.string().required("This value is required").nullable(),
        aifCategoryType: Yup.string().required("AIF Category Type is required").nullable(),
        targetReturnIRR: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).typeError("Target Return must be a number").required("Target Return is required").min(0, "Negative values not allowed").max(100, "Percentage cannot exceed 100"),
        fundSetupCost: standardNumericValidation("Fund set up cost"),
        otherExpenses: Yup.number().transform((val) => (isNaN(val) ? undefined : val)).typeError("Other Expenses must be a number").required("Other Expenses is required").min(0, "Negative values not allowed").max(100, "Percentage cannot exceed 100"),
        //justificationForHurdleCarryInterestRate: Yup.string().required("Justification for Hurdle and carry interest rate is required").nullable(),
    });

    const {
        control,
        register,
        handleSubmit,
        getValues,
        setValue,
        reset,
        watch,
        formState: { errors, isValid },
    } = useForm<IPrelimApplicationData>({
        resolver: yupResolver(validationSchema),
        mode: "all",
        shouldFocusError: false,
        defaultValues: prelimApplicationState.prelimApplication || {}
    });

    const buildPrelimPayload = (data: IPrelimApplicationData): IPrelimApplicationData => ({
        ...data,
        fundManager: firstTimeManagerType ? "First Time Fund Manager" : data.fundManager
    });

    const onSubmit = (data: IPrelimApplicationData) => {
        console.log(data);
        savePrelimApplicationForm(buildPrelimPayload(data), false);
    };

    // errors && console.log('errors', JSON.stringify(errors));

    useImperativeHandle(ref, () => ({
        submit: async (opts?: { silent?: boolean }) => {
            let isValid = false;
            await handleSubmit(
                async (data) => {
                    console.log(data);
                    await savePrelimApplicationForm(buildPrelimPayload(data), Boolean(opts?.silent));
                    isValid = true;
                },
                () => {
                    isValid = false;
                }
            )();
            return isValid;
        }
    }));

    const internalButtonSx = {
        backgroundColor: '#FF671F',
        color: 'white',
        textTransform: 'none',
        borderRadius: '8px',
        px: 4,
        '&:hover': {
            border: '1px solid #FF671F',
            color: '#FF671F',
            backgroundColor: 'rgb(255 103 30 / 19%)'
        }
    };

    if (prelimApplicationState.status.fetchStatus === FetchStatus.FAILED)
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="error" gutterBottom>
                    Failed to load data
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {prelimApplicationState.status.message || "An unexpected error occurred while fetching application data."}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        if (Number(prelimAppicationId)) {
                            dispatch(getPrelimApplicationData(
                                wrapArgument(actionUid, Number(prelimAppicationId))
                            ))
                        }
                    }}
                >
                    Retry
                </Button>
            </Box>
        );

    if (prelimApplicationState.status.fetchStatus === FetchStatus.IDLE || prelimApplicationState.prelimApplication?.id)
        return (
            // <form onSubmit={savePrelimApplicationForm}>
            <Box component="form" sx={{ p: 0 }}>
                <Grid container spacing={3}>
                    {/* <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            id="investmentStrategy"
                            label="Investment Strategy"
                            placeholder="Enter the investment strategy..."
                            value={prelimApplicationFormData.investmentStrategy || ''}
                            {...register("investmentStrategy")}
                            error={!!errors.investmentStrategy}
                            helperText={errors.investmentStrategy?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            multiline
                            maxRows={4}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Grid> */}
                    {/* <FormControl variant="standard" sx={{ display: 'flex', ml: 2 }}>
                            <InputLabel id="demo-simple-select-standard-label">Scheme</InputLabel>
                            <Select
                                labelId="scheme"
                                id="scheme"
                                value={String(prelimApplicationFormData.scheme || '')}
                                {...register("scheme")}
                                error={(errors.scheme && getValues("scheme") == '') ? true : false}
                                onChange={handleChange}
                                name="scheme"
                            >
                                <MenuItem key={"Fund of funds"} value={"Fund of funds"} selected={String(prelimApplicationFormData.scheme || '') === "Fund of funds"}>Fund of funds for startups</MenuItem>
                                <MenuItem key={"Aspire for MSME"} value={"Aspire for MSME"} selected={String(prelimApplicationFormData.scheme || '') === "Aspire for MSME"}>Aspire for MSME</MenuItem>
                                <MenuItem key={"UP Start-up Fund"} value={"UP Start-up Fund"} selected={String(prelimApplicationFormData.scheme || '') === "UP Start-up Fund"}>UP Start-up Fund</MenuItem>
                                <MenuItem key={"Odisha Startup Fund"} value={"Odisha Startup Fund"} selected={String(prelimApplicationFormData.scheme || '') === "Odisha Startup Fund"}>Odisha Startup Fund</MenuItem>
                                <MenuItem key={"Bihar Startup Scale-up financing fund"} value={"Bihar Startup Scale-up financing fund"} selected={String(prelimApplicationFormData.scheme || '') === "Bihar Startup Scale-up financing fund"}>Bihar Startup Scale-Up Financing Fund</MenuItem>
                                <MenuItem key={"MSInS Fund of Funds for Startups"} value={"MSInS Fund of Funds for Startups"} selected={String(prelimApplicationFormData.scheme || '') === "MSInS Fund of Funds for Startups"}>MSInS Fund of Funds for Startups</MenuItem>
                            </Select>
                            <Typography variant="caption" color="error">
                            <>{(errors.scheme && getValues("scheme") == '')?errors.scheme.message : ''}</>
                            </Typography>
                        </FormControl> */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            InputProps={{
                                readOnly: true,
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            required
                            fullWidth
                            id="nameOfTheFund"
                            label="Name of the Fund"
                            value={prelimApplicationFormData.nameOfTheFund || aifNameData}
                            {...register("nameOfTheFund")}
                            error={!!errors.nameOfTheFund}
                            helperText={errors.nameOfTheFund?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            sx={{ ...fieldSx, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            InputProps={{
                                readOnly: true,
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            required
                            fullWidth
                            id="scheme"
                            label="Scheme Name"
                            value={String(prelimApplicationFormData.scheme || selectedScheme || '')}
                            {...register("scheme")}
                            error={!!errors.scheme}
                            helperText={errors.scheme?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            sx={{ ...fieldSx, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            id="sponsor"
                            label="Sponsor"
                            value={prelimApplicationFormData.sponsor || ''}
                            {...register("sponsor")}
                            error={!!errors.sponsor}
                            helperText={errors.sponsor?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            sx={fieldSx}
                            inputProps={{ maxLength: FIELD_LIMITS.SHORT_TEXT }}
                        />
                        {getCharCount(prelimApplicationFormData.sponsor, FIELD_LIMITS.SHORT_TEXT)}
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            id="investmentManager"
                            label="Investment Manager (IM)/AMC"
                            value={prelimApplicationFormData.investmentManager || ''}
                            {...register("investmentManager")}
                            error={!!errors.investmentManager}
                            helperText={errors.investmentManager?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            sx={fieldSx}
                            inputProps={{ maxLength: FIELD_LIMITS.SHORT_TEXT }}
                        />
                        {getCharCount(prelimApplicationFormData.investmentManager, FIELD_LIMITS.SHORT_TEXT)}
                    </Grid>

                    <Grid item xs={12} md={4}>
                        {/* <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                            <InputLabel id="fundManager-label" sx={{ backgroundColor: 'white', px: 0.5 }}>Fund Manager</InputLabel>
                            <MasterData propertyType="fundManager"
                                propertyValue={prelimApplicationFormData.fundManager || 0}
                                onChange={handleSelectChange} />
                        </FormControl> */}
                        <FormControl fullWidth variant="outlined" error={!!errors.fundManager} sx={{ ...fieldSx, display: 'flex' }}>
                            <InputLabel id="fundManager-label"
                                sx={{ backgroundColor: 'white', px: 0.5, borderRadius: '8px' }}
                            >Fund Manager Experience</InputLabel>
                            <Controller
                                name="fundManager"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="fundManager-label"
                                        id="fundManager"
                                        value={firstTimeManagerType ? "First Time Fund Manager" : (field.value || "")}
                                        disabled={firstTimeManagerType}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            handleChange({
                                                preventDefault: () => { },
                                                target: { name: 'fundManager', value: e.target.value }
                                            });
                                        }}
                                    >
                                        {fundManagerOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.fundManager && (
                                <FormHelperText>{errors.fundManager.message as string}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    {/* <Grid item xs={4}>
                        <FormControl variant="standard" sx={{ display: 'flex' }}>
                            <InputLabel id="demo-simple-select-standard-label">Deal Type</InputLabel>
                            <MasterData propertyType="dealType"
                                propertyValue={prelimApplicationFormData.dealType || 0}
                                onChange={handleSelectChange} />
                        </FormControl>

                    </Grid>

                    <Grid item xs={4}>

                        <FormControl variant="standard" sx={{ display: 'flex' }}>
                            <InputLabel id="demo-simple-select-standard-label">Impact Fund</InputLabel>
                            <MasterData propertyType="impact"
                                propertyValue={prelimApplicationFormData.impact || 0}
                                onChange={handleSelectChange} />
                        </FormControl>

                    </Grid> */}

                    <Grid item xs={12} md={4}>
                        {/* <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                            <InputLabel id="aifCategory-label" sx={{ backgroundColor: 'white', px: 0.5 }}>AIF Category</InputLabel>
                            <MasterData propertyType="aifCategory"
                                propertyValue={prelimApplicationFormData.aifCategory || 0}
                                onChange={handleSelectChange} />
                        </FormControl> */}
                        <FormControl fullWidth variant="outlined" error={!!errors.aifCategory} sx={{ ...fieldSx, display: 'flex' }}>
                            <InputLabel id="aifCategory-label"
                                sx={{ backgroundColor: 'white', px: 0.5, borderRadius: '8px' }}>AIF Category</InputLabel>
                            <Controller
                                name="aifCategory"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="aifCategory-label"
                                        id="aifCategory"
                                        value={field.value || ""}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            handleChange({
                                                preventDefault: () => { },
                                                target: { name: 'aifCategory', value: e.target.value }
                                            });
                                        }}
                                    >
                                        <MenuItem value={"Category I"}>Category I</MenuItem>
                                        <MenuItem value={"Category II"}>Category II</MenuItem>
                                    </Select>
                                )}
                            />
                            {errors.aifCategory && (
                                <FormHelperText>{errors.aifCategory.message as string}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Controller
                            name="aifCategoryType"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="AIF Category Type"
                                    value={prelimApplicationFormData.aifCategoryType || ''}
                                    error={!!errors.aifCategoryType}
                                    helperText={errors.aifCategoryType?.message as string}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                    sx={fieldSx}
                                />
                            )}
                        />
                    </Grid>


                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            id="nameOfTrustee"
                            label="Name Of the Trustee"
                            value={prelimApplicationFormData.nameOfTrustee || ''}
                            {...register("nameOfTrustee")}
                            error={!!errors.nameOfTrustee}
                            helperText={errors.nameOfTrustee?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            sx={fieldSx}
                            inputProps={{ maxLength: FIELD_LIMITS.SHORT_TEXT }}
                        />
                        {getCharCount(prelimApplicationFormData.nameOfTrustee, FIELD_LIMITS.SHORT_TEXT)}
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Controller
                                name="dateOfFilingWithSEBI"
                                control={control}
                                rules={{
                                    required: "This value is required",
                                    validate: (value: Dayjs | null) => {
                                        if (!value) return true;
                                        return value.isBefore(MIN_DATE)
                                            ? "Date cannot be before 01/01/2020"
                                            : true;
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <DesktopDatePicker
                                        label="Date of Approval of PPM by SEBI"
                                        inputFormat="DD/MM/YYYY"
                                        disableFuture
                                        minDate={MIN_DATE}
                                        maxDate={MAX_DATE}
                                        shouldDisableDate={(d) =>
                                            dayjs(d).isBefore(MIN_DATE, 'day') || dayjs(d).isAfter(MAX_DATE, 'day')
                                        }
                                        value={field.value || null}
                                        onChange={(newValue: Dayjs | null) => {
                                            field.onChange(newValue);
                                            setDateValue("dateOfFilingWithSEBI", newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            id="contributionSought"
                            label="Contribution Sought (₹ Crore)"
                            value={prelimApplicationFormData.contributionSought ?? ''}
                            {...register("contributionSought")}
                            error={!!errors.contributionSought}
                            helperText={
                                errors.contributionSought
                                    ? (errors.contributionSought.message as string)
                                    : (hasNumericFormValue(prelimApplicationFormData?.contributionSought)
                                        ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.contributionSought)) * 10000000)
                                        : '')
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="termOfFund"
                            label="Term of the Fund (Years)"
                            {...register("termOfFund")}
                            error={!!errors.termOfFund}
                            helperText={errors.termOfFund?.message as string}
                            value={prelimApplicationFormData.termOfFund ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="commitmentPeriod"
                            label="Commitment Period (Years)"
                            value={prelimApplicationFormData.commitmentPeriod ?? ''}
                            {...register("commitmentPeriod")}
                            error={!!errors.commitmentPeriod}
                            helperText={errors.commitmentPeriod?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                            inputProps={{ min: 0, max: 10, step: 0.01 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="preferredReturn"
                            label="Preferred Return/Hurdle Rate p.a. (%)"
                            value={prelimApplicationFormData.preferredReturn ?? ''}
                            {...register("preferredReturn")}
                            error={!!errors.preferredReturn}
                            helperText={errors.preferredReturn?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="targetReturnIRR"
                            label="Target Gross Return of the Fund (IRR in %)"
                            value={prelimApplicationFormData.targetReturnIRR ?? ''}
                            {...register("targetReturnIRR")}
                            error={!!errors.targetReturnIRR}
                            helperText={errors.targetReturnIRR?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="managementFees"
                            label="Management Fee p.a. (%)"
                            value={prelimApplicationFormData.managementFees ?? ''}
                            {...register("managementFees")}
                            error={!!errors.managementFees}
                            helperText={errors.managementFees?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="fundSetupCost"
                            label="Fund Set Up Cost (%)"
                            value={prelimApplicationFormData.fundSetupCost ?? ''}
                            {...register("fundSetupCost")}
                            error={!!errors.fundSetupCost}
                            helperText={errors.fundSetupCost?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="otherExpenses"
                            label="Operating expenses p.a. %"
                            value={prelimApplicationFormData.otherExpenses ?? ''}
                            {...register("otherExpenses")}
                            error={!!errors.otherExpenses}
                            helperText={errors.otherExpenses?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required
                            fullWidth
                            type="number" onKeyDown={blockOverflowNumericInput}
                            id="carriedInterest"
                            label="Carried Interest (%)"
                            value={prelimApplicationFormData.carriedInterest ?? ''}
                            {...register("carriedInterest")}
                            error={!!errors.carriedInterest}
                            helperText={errors.carriedInterest?.message as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {/* <FormControl variant="outlined" sx={{ display: 'flex', borderRadius: '8px' }}>
                            <InputLabel id="demo-simple-select-standard-label"
                                sx={{ backgroundColor: 'white', px: 0.5, borderRadius: '8px' }}
                            >Deal Sector</InputLabel>
                            <Select
                                labelId="dealSector"
                                id="dealSector"
                                value={prelimApplicationFormData.dealSector || ""}
                                onChange={handleChange}
                                name="dealSector"
                            // defaultValue={prelimApplicationFormData.dealSector || ""}
                            >
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Automobile And Auto Components"} 
                                key={"Automobile And Auto Components"} value={"Automobile And Auto Components"}>Automobile And Auto Components</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Capital Goods"} 
                                key={"Capital Goods"} value={"Capital Goods"}>Capital Goods</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Chemicals"} 
                                key={"Chemicals"} value={"Chemicals"}>Chemicals</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Construction & Construction Materials"} 
                                key={"Construction & Construction Materials"} value={"Construction & Construction Materials"}>Construction & Construction Materials</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Consumer Services"} 
                                key={"Consumer Services"} value={"Consumer Services"}>Consumer Services</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Fast Moving Consumer Goods"} 
                                key={"Fast Moving Consumer Goods"} value={"Fast Moving Consumer Goods"}>Fast Moving Consumer Goods</MenuItem>
                                <MenuItem
                                // selected={prelimApplicationFormData.dealSector === "Financial Services"}
                                key={"Financial Services"}
                                value={"Financial Services"}>
                                    Financial Services
                                </MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Healthcare"}
                                key={"Healthcare"} value={"Healthcare"}>Healthcare</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Information Technology"}
                                key={"Information Technology"} 
                                value={"Information Technology"}>
                                    Information Technology
                                </MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Metals & Mining"} 
                                key={"Metals & Mining"} value={"Metals & Mining"}>Metals & Mining</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Oil, Gas & Consumable Fuels"} 
                                key={"Oil, Gas & Consumable Fuels"} value={"Oil, Gas & Consumable Fuels"}>Oil, Gas & Consumable Fuels</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Power"} 
                                key={"Power"} value={"Power"}>Power</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Realty"} 
                                key={"Realty"} value={"Realty"}>Realty</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Services"} 
                                key={"Services"} value={"Services"}>Services</MenuItem>
                                <MenuItem
                                // selected={prelimApplicationFormData.dealSector === "Telecommunication"}
                                key={"Telecommunication"} value={"Telecommunication"}>Telecommunication</MenuItem>
                                <MenuItem 
                                // selected={prelimApplicationFormData.dealSector === "Textiles"} 
                                key={"Textiles"} value={"Textiles"}>Textiles</MenuItem>
                            </Select>
                        </FormControl> */}
                        <TextField
                            fullWidth
                            id="dealSector"
                            label="Deal Sector"
                            value={prelimApplicationFormData.dealSector || ''}
                            {...register("dealSector")}
                            error={!!errors.dealSector}
                            helperText={errors.dealSector?.message as string}
                            onChange={(e) => {
                                handleChange(e);
                            }}
                            variant="outlined"
                            sx={fieldSx}
                        />
                        {getWordCount(watch("dealSector"), FIELD_LIMITS.LONG_TEXT)}
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField
                            required
                            fullWidth
                            id="description"
                            label="Sector Description/Stage"
                            maxRows={4}
                            placeholder="e.g. Seed, Series A..."
                            value={prelimApplicationFormData.description || ''}
                            {...register("description")}
                            error={!!errors.description}
                            helperText={errors.description?.message as string}
                            onChange={handleChange}
                            variant="outlined"
                            sx={fieldSx}
                        />
                        {getWordCount(watch("description"), FIELD_LIMITS.LONG_TEXT)}
                    </Grid>

                    {/* <Grid item xs={12}>
                        <TextField
                            required
                            id="investmentStrategy"
                            label="Investment Strategy"
                            value={prelimApplicationFormData.investmentStrategy || ''}
                            {...register("investmentStrategy")}
                            error={errors.investmentStrategy ? true : false}
                            onChange={handleChange}
                            variant="standard"
                            multiline
                            sx={{ display: 'flex', ml: 2, mr: 2 }}
                        />
                        <Typography variant="caption" color="error" sx={{ ml: '20px' }}>
                            <>{errors.investmentStrategy?errors.investmentStrategy.message : ''}</>
                        </Typography>
                    </Grid> */}
                    <Grid item xs={12}>
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#363062', mb: 3 }}>
                                Capital & Funding Details
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdDescription"
                                        label="Capital Raised Till Date (₹ Crore)"
                                        value={prelimApplicationFormData.sdDescription ?? ''}
                                        {...register("sdDescription")}
                                        error={!!errors.sdDescription}
                                        helperText={
                                            errors.sdDescription
                                                ? (errors.sdDescription.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdDescription)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdDescription)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                        inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Target Corpus" sx={{ fontWeight: 700, backgroundColor: 'rgba(54, 48, 98, 0.05)', color: '#363062' }} />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdTargetCorpusDomestic"
                                        label="Domestic (₹ Crore)"
                                        value={prelimApplicationFormData.sdTargetCorpusDomestic ?? ''}
                                        {...register("sdTargetCorpusDomestic")}
                                        error={!!errors.sdTargetCorpusDomestic}
                                        helperText={
                                            errors.sdTargetCorpusDomestic
                                                ? (errors.sdTargetCorpusDomestic.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdTargetCorpusDomestic)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdTargetCorpusDomestic)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdTargetCorpusOverseas"
                                        label="Overseas (₹ Crore)"
                                        value={prelimApplicationFormData.sdTargetCorpusOverseas ?? ''}
                                        {...register("sdTargetCorpusOverseas")}
                                        error={!!errors.sdTargetCorpusOverseas}
                                        helperText={
                                            errors.sdTargetCorpusOverseas
                                                ? (errors.sdTargetCorpusOverseas.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdTargetCorpusOverseas)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdTargetCorpusOverseas)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdTotalTargetCorpus"
                                        label="Total Target Corpus (₹ Crore)"
                                        value={prelimApplicationFormData.sdTotalTargetCorpus ?? ''}
                                        {...register("sdTotalTargetCorpus")}
                                        error={!!errors.sdTotalTargetCorpus}
                                        helperText={
                                            errors.sdTotalTargetCorpus
                                                ? (errors.sdTotalTargetCorpus.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdTotalTargetCorpus)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdTotalTargetCorpus)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Target Corpus (Green Shoe)" sx={{ fontWeight: 700, backgroundColor: 'rgba(54, 48, 98, 0.05)', color: '#363062' }} />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdGreenShoeTargetCorpusDomestic"
                                        label="Domestic (Green Shoe) (₹ Crore)"
                                        value={prelimApplicationFormData.sdGreenShoeTargetCorpusDomestic ?? ''}
                                        {...register("sdGreenShoeTargetCorpusDomestic")}
                                        error={!!errors.sdGreenShoeTargetCorpusDomestic}
                                        helperText={
                                            errors.sdGreenShoeTargetCorpusDomestic
                                                ? (errors.sdGreenShoeTargetCorpusDomestic.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdGreenShoeTargetCorpusDomestic)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdGreenShoeTargetCorpusDomestic)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdGreenShoeTargetCorpusOverseas"
                                        label="Overseas (Green Shoe) (₹ Crore)"
                                        value={prelimApplicationFormData.sdGreenShoeTargetCorpusOverseas ?? ''}
                                        {...register("sdGreenShoeTargetCorpusOverseas")}
                                        error={!!errors.sdGreenShoeTargetCorpusOverseas}
                                        helperText={
                                            errors.sdGreenShoeTargetCorpusOverseas
                                                ? (errors.sdGreenShoeTargetCorpusOverseas.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdGreenShoeTargetCorpusOverseas)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdGreenShoeTargetCorpusOverseas)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdGreenShoeTotalTargetCorpus"
                                        label="Total (Green Shoe) (₹ Crore)"
                                        value={prelimApplicationFormData.sdGreenShoeTotalTargetCorpus ?? ''}
                                        {...register("sdGreenShoeTotalTargetCorpus")}
                                        error={!!errors.sdGreenShoeTotalTargetCorpus}
                                        helperText={
                                            errors.sdGreenShoeTotalTargetCorpus
                                                ? (errors.sdGreenShoeTotalTargetCorpus.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdGreenShoeTotalTargetCorpus)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdGreenShoeTotalTargetCorpus)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: { xs: 1, sm: 2 },
                                        p: 2,
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(54, 48, 98, 0.02)',
                                        border: '1px dashed rgba(54, 48, 98, 0.2)',
                                        mt: 2
                                    }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#363062', flexShrink: 0 }}>
                                            First Closing Confirmed?
                                        </Typography>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                row
                                                value={String(!!prelimApplicationFormData.firstClosing)}
                                                onChange={(e) => handleFirstClosingChange(e.target.value === 'true')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Radio value="true" size="small" sx={controlSx} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Yes</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Radio value="false" size="small" sx={controlSx} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>No</Typography>
                                                    </Box>
                                                </Box>
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdFirstClosingDomesticAmount"
                                        label={prelimApplicationFormData.firstClosing ? "Domestic Amount (₹ Crore)" : "Expected Domestic Amount (₹ Crore)"}
                                        value={prelimApplicationFormData.sdFirstClosingDomesticAmount ?? ''}
                                        {...register("sdFirstClosingDomesticAmount")}
                                        error={!!errors.sdFirstClosingDomesticAmount}
                                        helperText={
                                            errors.sdFirstClosingDomesticAmount
                                                ? (errors.sdFirstClosingDomesticAmount.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdFirstClosingDomesticAmount)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdFirstClosingDomesticAmount)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number" onKeyDown={blockOverflowNumericInput}
                                        id="sdFirstClosingOverseasAmount"
                                        label={prelimApplicationFormData.firstClosing ? "Overseas Amount (₹ Crore)" : "Expected Overseas Amount (₹ Crore)"}
                                        value={prelimApplicationFormData.sdFirstClosingOverseasAmount ?? ''}
                                        {...register("sdFirstClosingOverseasAmount")}
                                        error={!!errors.sdFirstClosingOverseasAmount}
                                        helperText={
                                            errors.sdFirstClosingOverseasAmount
                                                ? (errors.sdFirstClosingOverseasAmount.message as string)
                                                : (hasNumericFormValue(prelimApplicationFormData?.sdFirstClosingOverseasAmount)
                                                    ? numberToWordsIndian(parseFloat(String(prelimApplicationFormData.sdFirstClosingOverseasAmount)) * 10000000)
                                                    : '')
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        sx={{ ...numericSx, '& .MuiFormLabel-asterisk': { display: 'none' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="sdFirstClosingDomesticAmountDate"
                                            control={control}
                                            rules={{
                                                validate: (value: Dayjs | null) => {
                                                    if (!value) return true;
                                                    return value.isBefore(MIN_DATE)
                                                        ? "Date cannot be before 01/01/2020"
                                                        : true;
                                                }
                                            }}
                                            render={({ fieldState: { invalid, error } }) => (
                                                <DesktopDatePicker
                                                    inputFormat="DD/MM/YYYY"
                                                    label={prelimApplicationFormData.firstClosing ? "Closing Date" : "Expected Closing Date"}
                                                    minDate={MIN_DATE}
                                                    shouldDisableDate={(d) => dayjs(d).isBefore(MIN_DATE, 'day')}
                                                    value={prelimApplicationFormData.sdFirstClosingDomesticAmountDate || null}
                                                    onChange={(newValue: any) => {
                                                        setValue('sdFirstClosingDomesticAmountDate', newValue);
                                                        setDateValue("sdFirstClosingDomesticAmountDate", newValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            fullWidth
                                                            variant="outlined"
                                                            {...params}
                                                            error={invalid}
                                                            helperText={error?.message || null}
                                                            sx={fieldSx}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                {/* <Grid item xs={12} md={4}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="sdFirstCorpusOverseasAmountDate"
                                            control={control}
                                            render={({ fieldState: { invalid } }) => (
                                                <DesktopDatePicker
                                                    inputFormat="DD/MM/YYYY"
                                                    label="Closing Date (Overseas)"
                                                    value={prelimApplicationFormData.sdFirstCorpusOverseasAmountDate || null}
                                                    onChange={(newValue: any) => {
                                                        setValue('sdFirstCorpusOverseasAmountDate', newValue);
                                                        setDateValue("sdFirstCorpusOverseasAmountDate", newValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            fullWidth
                                                            variant="outlined"
                                                            {...params}
                                                            error={invalid}
                                                            helperText={invalid ? "This value is required" : null}
                                                            sx={fieldSx}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid> */}

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            onClick={handleSubmit(onSubmit)}
                                            sx={internalButtonSx}
                                        >
                                            Save & Continue
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box >
        );
    else
        return <Box sx={{ p: 3, textAlign: 'center' }}><Typography>Loading...</Typography></Box>
});

export default PrelimApplicationData;
